import React, { useState, useEffect, useContext, useRef } from 'react';
import { Link } from 'react-router-dom';
import { AppContext } from '../context/AppContext';
import { useTranslation } from 'react-i18next';
import JsBarcode from 'jsbarcode';
import Modal from '../components/Modal';

const StockIntake = () => {
    const { authFetch, depos, selectedDepo } = useContext(AppContext);
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [localDepo, setLocalDepo] = useState(selectedDepo !== 'Global' ? selectedDepo : (depos[0]?.id || ''));
    const [supplier, setSupplier] = useState('');
    const [supplierId, setSupplierId] = useState(null);
    const [suppliersList, setSuppliersList] = useState([]);
    const [paymentStatus, setPaymentStatus] = useState('Paid');
    const [catalog, setCatalog] = useState([]);
    const [catalogLoading, setCatalogLoading] = useState(false);
    const [productSearch, setProductSearch] = useState("");
    const [selectedProductIds, setSelectedProductIds] = useState([]);

    const [rows, setRows] = useState([
        { id: Date.now(), name: '', barcode: '', cost: '', profit: '20', price: '', qty: '1', sku: '', origin: '', currentQty: '—', suggestions: [], isMatched: false, created_at: new Date().toISOString() }
    ]);

    // Date filter state
    const [dateFilter, setDateFilter] = useState(new Date().toISOString().slice(0,10));

    const fetchCatalog = async () => {
        setCatalogLoading(true);
        try {
            const url = localDepo && localDepo !== 'Global' ? `/api/products?depo_id=${localDepo}` : '/api/products';
            const res = await authFetch(url);
            const data = await res.json();
            setCatalog(Array.isArray(data) ? data : []);
        } catch (e) {
            console.error("Failed to fetch catalog", e);
        } finally {
            setCatalogLoading(false);
        }
    };

    const fetchSuppliers = async () => {
        try {
            const res = await authFetch('/api/suppliers');
            if (res.ok) {
                const data = await res.json();
                setSuppliersList(Array.isArray(data) ? data : []);
            }
        } catch (e) { console.error("Failed to fetch suppliers", e); }
    };

    useEffect(() => {
        fetchCatalog();
        fetchSuppliers();
        
        // Refresh quantities for already selected rows when depo changes
        const refreshRows = async () => {
            const updatedRows = await Promise.all(rows.map(async (r) => {
                if (r.isMatched && (r.barcode || r.name)) {
                    try {
                        const depParam = localDepo && localDepo !== 'Global' ? `&depo_id=${localDepo}` : '';
                        const query = r.barcode || r.name;
                        const res = await authFetch(`/api/products?search=${encodeURIComponent(query)}${depParam}`);
                        const data = await res.json();
                        const prod = data.find(p => p.barcode === r.barcode || p.name === r.name);
                        if (prod) {
                            return { ...r, currentQty: localDepo && localDepo !== 'Global' ? (prod.quantity ?? 0) : (prod.total_quantity ?? 0) };
                        }
                    } catch (e) {}
                }
                return r;
            }));
            setRows(updatedRows);
        };
        
        if (rows.some(r => r.isMatched)) {
            refreshRows();
        }
    }, [localDepo]);

    const [isPrintModalOpen, setIsPrintModalOpen] = useState(false);

    const addRow = () => {
        setRows([
            ...rows,
            { id: Date.now(), name: '', barcode: '', cost: '', profit: '20', price: '', qty: '1', sku: '', origin: '', currentQty: '—', suggestions: [], isMatched: false, created_at: new Date().toISOString() }
        ]);
    };

    const removeRow = (id) => {
        if (rows.length > 1) setRows(rows.filter(r => r.id !== id));
    };

    const searchProduct = async (id, query) => {
        if (!query || query.length < 2) {
            updateRow(id, 'suggestions', []);
            return;
        }
        try {
            const depParam = localDepo && localDepo !== 'Global' ? `&depo_id=${localDepo}` : '';
            const res = await authFetch(`/api/products?search=${encodeURIComponent(query)}${depParam}`);
            const data = await res.json();
            
            // If it's an exact barcode match, select it immediately
            const exactMatch = Array.isArray(data) && data.find(p => p.barcode === query);
            if (exactMatch && query.length >= 4) {
                selectProduct(id, exactMatch);
            } else {
                updateRow(id, 'suggestions', Array.isArray(data) ? data : []);
            }
        } catch (e) {
            console.error("Search failed", e);
        }
    };

    const searchProductByBarcode = async (id, barcode) => {
        if (!barcode || barcode.length < 3) return;
        try {
            const depParam = localDepo && localDepo !== 'Global' ? `&depo_id=${localDepo}` : '';
            const res = await authFetch(`/api/products?search=${encodeURIComponent(barcode)}${depParam}`);
            const data = await res.json();
            const exactMatch = Array.isArray(data) && data.find(p => p.barcode === barcode);
            if (exactMatch) {
                selectProduct(id, exactMatch);
            }
        } catch (e) {
            console.error("Barcode search failed", e);
        }
    };

    const selectProduct = (rowId, prod) => {
        setRows(rows.map(r => {
            if (r.id === rowId) {
                return {
                    ...r,
                    name: prod.name,
                    barcode: prod.barcode || '',
                    sku: prod.sku || '',
                    origin: prod.origin || '',
                    cost: prod.cost_price || '',
                    price: prod.price || '',
                    currentQty: localDepo && localDepo !== 'Global' ? (prod.quantity ?? 0) : (prod.total_quantity ?? 0),
                    isMatched: true,
                    suggestions: []
                };
            }
            return r;
        }));
    };

    const updateRow = (id, field, value) => {
        setRows(rows.map(r => {
            if (r.id === id) {
                const newRow = { ...r, [field]: value };

                if (field === 'name' && !r.isMatched) {
                    searchProduct(id, value);
                }

                // If user edits name after match, reset match
                if (field === 'name' && r.isMatched && value !== r.name) {
                    newRow.isMatched = false;
                }

                // Auto-calculate Price if Cost or Profit changes
                if (field === 'cost' || field === 'profit') {
                    const cost = parseFloat(field === 'cost' ? value : r.cost) || 0;
                    const profit = parseFloat(field === 'profit' ? value : r.profit) || 0;
                    if (cost > 0) {
                        newRow.price = (cost + (cost * (profit / 100))).toFixed(2);
                    }
                }

                // Auto-calculate Profit if Price changes manually
                if (field === 'price') {
                    const cost = parseFloat(r.cost) || 0;
                    const price = parseFloat(value) || 0;
                    if (cost > 0 && price > 0) {
                        newRow.profit = (((price - cost) / cost) * 100).toFixed(1);
                    }
                }

                return newRow;
            }
            return r;
        }));
    };

    const generateBarcode = (id) => {
        const prefix = "200";
        let randomDigits = Math.floor(Math.random() * 1000000000).toString().padStart(9, '0');
        let code13 = prefix + randomDigits + "5";
        updateRow(id, 'barcode', code13);
    };

    const handleBarcodeChange = (id, value) => {
        updateRow(id, 'barcode', value);
        // Only search if length is typical for barcode to avoid spamming
        if (value.length >= 4) {
            searchProductByBarcode(id, value);
        }
    };

    const handleSaveIntake = async () => {
        if (!localDepo || localDepo === 'Global') return alert(t("Please select a specific warehouse."));

        setLoading(true);
        try {
            const purchaseItems = [];

            for (const row of rows) {
                if (!row.name || !row.cost || !row.qty) continue;

                let productId;
                // Double check match by barcode or name to prevent duplicates if user didn't select from dropdown
                const checkRes = await authFetch(`/api/products?search=${encodeURIComponent(row.barcode || row.name)}`);
                const existing = await checkRes.json();
                const matched = existing.find(p => p.barcode === row.barcode || p.name.toLowerCase() === row.name.toLowerCase());

                if (matched) {
                    productId = matched.id;
                    // Price sync is now handled by the backend via sell_price in the purchase payload
                } else {
                    // Create new product in Catalog
                    const newProdRes = await authFetch('/api/products', {
                        method: 'POST',
                        body: JSON.stringify({
                            name: row.name,
                            sku: row.sku || `SKU-${Date.now()}`,
                            barcode: row.barcode,
                            cost_price: row.cost,
                            price: row.price,
                            origin: row.origin,
                            category: 'General',
                            unit: 'pcs'
                        })
                    });
                    const newProd = await newProdRes.json();
                    productId = newProd.id;
                }

                purchaseItems.push({
                    product_id: productId,
                    quantity:   parseFloat(row.qty)   || 1,
                    unit_price: parseFloat(row.cost)  || 0,  // cost price
                    sell_price: parseFloat(row.price) || 0,  // selling price — backend will update products.price
                    line_total: (parseFloat(row.cost) || 0) * (parseFloat(row.qty) || 1),
                });
            }

            // 2. Submit Purchase
            if (purchaseItems.length > 0) {
                const totalCost = purchaseItems.reduce((s, i) => s + i.line_total, 0);
                await authFetch('/api/purchases', {
                    method: 'POST',
                    body: JSON.stringify({
                        supplier: supplier || 'N/A',
                        supplier_id: supplierId,
                        depo_id:  localDepo,
                        items:    purchaseItems,
                        status:   paymentStatus,
                        total_cost: totalCost
                    })
                });
                alert(t("Stock Intake Processed Successfully!"));
                setIsPrintModalOpen(true);
                // Reset form
                setRows([{ id: Date.now(), name: '', barcode: '', cost: '', profit: '20', price: '', qty: '1', sku: '', origin: '', currentQty: '—', suggestions: [], isMatched: false, created_at: new Date().toISOString() }]);
                setSupplier('');
                fetchCatalog();
            }

        } finally {
            setLoading(false);
        }
    };
    const handleQuickEditCost = async (prod, newCost) => {
        try {
            await authFetch(`/api/products/${prod.id}`, {
                method: 'PUT',
                body: JSON.stringify({ ...prod, cost_price: newCost })
            });
            setCatalog(catalog.map(p => p.id === prod.id ? { ...p, cost_price: newCost } : p));
        } catch (e) {
            alert(t("Update failed"));
        }
    };

    // Filtered catalog for search
    const filteredCatalog = catalog.filter(p => {
        if (!productSearch) return true;
        const q = productSearch.toLowerCase();
        return (
            (p.name && p.name.toLowerCase().includes(q)) ||
            (p.barcode && p.barcode.toLowerCase().includes(q)) ||
            (p.sku && p.sku.toLowerCase().includes(q))
        );
    });

    // Toggle product selection
    const toggleProductSelection = (id) => {
        setSelectedProductIds(ids =>
            ids.includes(id) ? ids.filter(pid => pid !== id) : [...ids, id]
        );
    };

    // Print handler — opens a popup window so no CSS conflicts with React root
    const handlePrintCatalog = () => {
        const depotName = depos.find(d => String(d.id) === String(localDepo))?.name || '—';
        const printRows = catalog.length > 0 ? catalog : rows.filter(r => r.name);

        let totalCost = 0, totalSale = 0;
        const rowsHtml = printRows.map((p, i) => {
            const cost  = parseFloat(p.cost_price || p.cost || 0);
            const price = parseFloat(p.price || 0);
            const qty   = parseFloat(p.qty || p.total_quantity || 1);
            const margin = cost > 0 ? (((price - cost) / cost) * 100).toFixed(1) + '%' : '—';
            const marginColor = cost > 0 && ((price-cost)/cost)*100 < 10 ? '#dc2626' : '#16a34a';
            totalCost  += cost;
            totalSale  += price;
            const bg = i % 2 === 0 ? '#f8fafc' : '#ffffff';
            const statusBg  = price > cost ? '#dcfce7' : '#fee2e2';
            const statusClr = price > cost ? '#15803d' : '#dc2626';
            const statusTxt = price > cost ? '✔ OK'   : '⚠ CHECK';
            return `
                <tr style="background:${bg}">
                    <td style="padding:4px 6px;border:1px solid #e2e8f0;color:#94a3b8">${i+1}</td>
                    <td style="padding:4px 6px;border:1px solid #e2e8f0;font-weight:700;text-transform:uppercase">${p.name || p.product_name || '—'}</td>
                    <td style="padding:4px 6px;border:1px solid #e2e8f0;font-family:monospace;font-size:9px;color:#64748b">${p.sku || '—'}</td>
                    <td style="padding:4px 6px;border:1px solid #e2e8f0;font-family:monospace;font-size:9px">${p.barcode || '—'}</td>
                    <td style="padding:4px 6px;border:1px solid #e2e8f0;text-transform:uppercase;font-size:9px;color:#6366f1">${p.origin || '—'}</td>
                    <td style="padding:4px 6px;border:1px solid #e2e8f0;text-align:right;color:#1d4ed8;font-weight:700">${cost.toFixed(2)}</td>
                    <td style="padding:4px 6px;border:1px solid #e2e8f0;text-align:right;color:${marginColor}">${margin}</td>
                    <td style="padding:4px 6px;border:1px solid #e2e8f0;text-align:right;color:#059669;font-weight:700">${price.toFixed(2)}</td>
                    <td style="padding:4px 6px;border:1px solid #e2e8f0;text-align:right;font-weight:700">${qty}</td>
                    <td style="padding:4px 6px;border:1px solid #e2e8f0">
                        <span style="background:${statusBg};color:${statusClr};border-radius:4px;padding:1px 5px;font-size:9px;font-weight:700">${statusTxt}</span>
                    </td>
                </tr>`;
        }).join('');

        const avgMargin = (() => {
            const valid = catalog.filter(p => parseFloat(p.cost_price) > 0);
            if (!valid.length) return '—';
            const avg = valid.reduce((s, p) => s + (((parseFloat(p.price) - parseFloat(p.cost_price)) / parseFloat(p.cost_price)) * 100), 0) / valid.length;
            return avg.toFixed(1) + '%';
        })();

        const summaryBoxes = [
            { label: 'Total Products',    value: printRows.length,              color: '#1e293b' },
            { label: t('Total Cost Value'),  value: totalCost.toFixed(2) + ' CFA',  color: '#1d4ed8' },
            { label: t('Total Sale Value'),  value: totalSale.toFixed(2) + ' CFA',  color: '#059669' },
            { label: 'Average Margin',    value: avgMargin,                      color: '#7c3aed' },
        ].map(b => `
            <div style="flex:1;border:1px solid #e2e8f0;border-radius:6px;padding:8px 10px;background:#f8fafc">
                <div style="font-size:9px;color:#94a3b8;text-transform:uppercase;letter-spacing:1px;margin-bottom:2px">${b.label}</div>
                <div style="font-size:14px;font-weight:900;color:${b.color}">${b.value}</div>
            </div>`).join('');

        const signatureBoxes = ['Prepared By', 'Verified By', 'Manager Approval'].map(label => `
            <div style="text-align:center;width:28%">
                <div style="border-bottom:1px solid #334155;margin-bottom:4px;height:32px"></div>
                <div style="font-size:9px;color:#64748b;font-weight:700">${label}</div>
                <div style="font-size:8px;color:#94a3b8">Name &amp; Date</div>
            </div>`).join('');

        const html = `<!DOCTYPE html><html><head>
            <meta charset="utf-8">
            <title>Product Intake Report — ${dateFilter}</title>
            <style>
                * { box-sizing: border-box; margin: 0; padding: 0; }
                body { font-family: Arial, sans-serif; font-size: 11px; color: #111; padding: 20px; }
                @page { size: A4 portrait; margin: 10mm; }
                @media print { body { padding: 0; } }
            </style>
        </head><body>
            <div style="display:flex;justify-content:space-between;align-items:flex-start;border-bottom:2px solid #1e293b;padding-bottom:10px;margin-bottom:12px">
                <div>
                    <div style="font-size:18px;font-weight:900;letter-spacing:1px;text-transform:uppercase">YOUR COMPANY NAME</div>
                    <div style="font-size:10px;color:#64748b;margin-top:2px">Société d'importation et de commerce</div>
                    <div style="font-size:10px;color:#64748b">Tel: +213 000 000 000</div>
                </div>
                <div style="text-align:right">
                    <div style="font-size:14px;font-weight:900;color:#1e293b;text-transform:uppercase">Daily Product Intake Report</div>
                    <div style="font-size:10px;color:#475569;margin-top:2px">Date: <b>${dateFilter}</b></div>
                    <div style="font-size:10px;color:#475569">Supplier: <b>${supplier || 'All Suppliers'}</b></div>
                    <div style="font-size:10px;color:#475569">Depot: <b>${depotName}</b></div>
                    <div style="font-size:10px;color:#475569">Printed: <b>${new Date().toLocaleString()}</b></div>
                </div>
            </div>

            <div className="w-full overflow-x-auto max-w-full">
<table style="width:100%;border-collapse:collapse;font-size:10px;margin-bottom:14px">
                <thead>
                    <tr style="background:#1e293b;color:#fff">
                        <th style="padding:5px 6px;text-align:left;border:1px solid #334155">#</th>
                        <th style="padding:5px 6px;text-align:left;border:1px solid #334155">Product Name</th>
                        <th style="padding:5px 6px;text-align:left;border:1px solid #334155">Reference</th>
                        <th style="padding:5px 6px;text-align:left;border:1px solid #334155">Barcode</th>
                        <th style="padding:5px 6px;text-align:left;border:1px solid #334155">Origin</th>
                        <th style="padding:5px 6px;text-align:right;border:1px solid #334155">Cost Price</th>
                        <th style="padding:5px 6px;text-align:right;border:1px solid #334155">Margin %</th>
                        <th style="padding:5px 6px;text-align:right;border:1px solid #334155">Sale Price</th>
                        <th style="padding:5px 6px;text-align:right;border:1px solid #334155">Qty</th>
                        <th style="padding:5px 6px;text-align:left;border:1px solid #334155">Status</th>
                    </tr>
                </thead>
                <tbody>
                    ${rowsHtml || '<tr><td colspan="10" style="text-align:center;padding:24px;color:#94a3b8;font-style:italic">No products found.</td></tr>'}
                    <tr style="background:#f1f5f9;font-weight:700">
                        <td colspan="5" style="padding:5px 6px;border:1px solid #e2e8f0;text-align:right;color:#475569">TOTALS (${printRows.length} items)</td>
                        <td style="padding:5px 6px;border:1px solid #e2e8f0;text-align:right;color:#1d4ed8">${totalCost.toFixed(2)}</td>
                        <td style="padding:5px 6px;border:1px solid #e2e8f0"></td>
                        <td style="padding:5px 6px;border:1px solid #e2e8f0;text-align:right;color:#059669">${totalSale.toFixed(2)}</td>
                        <td colspan="2" style="padding:5px 6px;border:1px solid #e2e8f0"></td>
                    </tr>
                </tbody>
            </table>
</div>

            <div style="display:flex;gap:12px;margin-bottom:16px">${summaryBoxes}</div>

            <div style="display:flex;justify-content:space-between;margin-top:24px;border-top:1px solid #e2e8f0;padding-top:16px">${signatureBoxes}</div>

            <div style="margin-top:14px;border-top:1px solid #e2e8f0;padding-top:6px;display:flex;justify-content:space-between;font-size:9px;color:#94a3b8">
                <span>Generated by ERP System &middot; ${new Date().toLocaleString()}</span>
                <span>CONFIDENTIAL &mdash; Internal Use Only</span>
            </div>

            <script>window.onload = function() { window.print(); window.onafterprint = function() { window.close(); }; }</script>
        </body></html>`;

        const popup = window.open('', '_blank', 'width=900,height=700,scrollbars=yes');
        if (popup) {
            popup.document.write(html);
            popup.document.close();
        } else {
            alert('Please allow popups for this site to print the report.');
        }
    };

    return (
        <div className="max-w-7xl mx-auto pb-20">
            <style>
                {`
                    .custom-shadow { box-shadow: 0 10px 40px -10px rgba(0,0,0,0.1); }
                `}
            </style>

            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4 bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                <div>
                    <h2 className="text-2xl font-bold text-gray-900">{t('Product Intake')}</h2>
                    <p className="text-slate-500 font-medium">{t('Receive stock items into catalog while managing cost prices')}</p>
                </div>
                <div className="flex items-center gap-4">
                    <Link
                        to="/products"
                        className="text-xs font-black uppercase text-indigo-600 hover:text-indigo-800 transition py-2 px-4 bg-indigo-50 rounded-xl"
                    >
                        📂 {t('View All Products')}
                    </Link>
                    <div className="bg-slate-50 p-3 rounded-2xl border border-slate-100 flex items-center gap-3">
                        <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest">{t('Target Warehouse')}:</label>
                        <select
                            value={localDepo}
                            onChange={e => setLocalDepo(e.target.value)}
                            className="bg-white border-none text-sm font-bold text-indigo-600 focus:ring-0 cursor-pointer outline-none"
                        >
                            <option value="">{t('Select Warehouse')}</option>
                            {depos.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                        </select>
                    </div>
                </div>
            </div>

            {/* Date Filter and Print Button BELOW header, before table */}
            <div className="flex flex-wrap items-center gap-4 mb-6">
                <label className="font-bold text-sm">{t('Filter by Date')}:</label>
                <input
                    type="date"
                    value={dateFilter}
                    onChange={e => setDateFilter(e.target.value)}
                    className="border rounded px-2 py-1 text-sm"
                />
                <button
                    onClick={handlePrintCatalog}
                    className="bg-blue-600 text-white px-4 py-2 rounded shadow hover:bg-blue-700 transition"
                >
                    {t('Print Report')}
                </button>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-visible">
                <div className="p-6 border-b bg-slate-50/50 flex flex-col md:flex-row gap-4 items-center">
                    <div className="flex-1 w-full md:w-1/2">
                        <label className="text-[10px] font-black uppercase text-slate-400 mb-1 block ltr:ml-1 rtl:mr-1">{t('Target Warehouse / Depo')}</label>
                        <select
                            value={localDepo}
                            onChange={e => setLocalDepo(e.target.value)}
                            className="w-full bg-white border border-slate-200 p-3 rounded-xl font-bold text-indigo-700 focus:border-indigo-500 outline-none transition shadow-sm cursor-pointer"
                        >
                            <option value="">{t('Select Warehouse')}</option>
                            {depos.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                        </select>
                    </div>
                    {/* Supplier Group */}
                    <div className="flex-1 min-w-[250px]">
                        <label className="text-[10px] font-black uppercase text-slate-400 mb-1 block ltr:ml-1 rtl:mr-1">{t('Supplier Group / Event')}</label>
                        <select
                            value={supplierId || ''}
                            onChange={e => {
                                const selected = suppliersList.find(s => String(s.id) === e.target.value);
                                setSupplierId(selected ? selected.id : null);
                                setSupplier(selected ? selected.name : '');
                            }}
                            className="w-full bg-white border-2 border-slate-100 rounded-xl px-4 py-3 text-slate-700 font-bold outline-none focus:border-indigo-400 focus:ring-4 focus:ring-indigo-50 transition-all shadow-sm appearance-none"
                            style={{ backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%2394a3b8' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`, backgroundPosition: `right 1rem center`, backgroundRepeat: `no-repeat`, backgroundSize: `1.5em 1.5em` }}
                        >
                            <option value="">{t('Select Supplier...')}</option>
                            {suppliersList.map(s => (
                                <option key={s.id} value={s.id}>{s.name}</option>
                            ))}
                        </select>
                    </div>

                    {/* Payment Status */}
                    <div className="w-[180px]">
                        <label className="text-[10px] font-black uppercase text-slate-400 mb-1 block ltr:ml-1 rtl:mr-1">{t('Payment Status')}</label>
                        <select
                            value={paymentStatus}
                            onChange={e => setPaymentStatus(e.target.value)}
                            className="w-full bg-white border-2 border-slate-100 rounded-xl px-4 py-3 text-slate-700 font-bold outline-none focus:border-indigo-400 focus:ring-4 focus:ring-indigo-50 transition-all shadow-sm appearance-none"
                            style={{ backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%2394a3b8' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`, backgroundPosition: `right 1rem center`, backgroundRepeat: `no-repeat`, backgroundSize: `1.5em 1.5em` }}
                        >
                            <option value="Paid">{t('Paid')}</option>
                            <option value="Credit">{t('On Credit')}</option>
                            <option value="Draft">{t('Draft')}</option>
                        </select>
                    </div>
                </div>

                <div className="overflow-visible">
                    <div className="w-full overflow-x-auto max-w-full">
<table className="w-full text-left">
                        <thead className="bg-slate-900 text-slate-400 text-[10px] font-black uppercase tracking-widest">
                            <tr>
                                <th className="p-4 ltr:pl-8 rtl:pr-8">{t('Product Details')}</th>
                                <th className="p-4">{t('Origin')}</th>
                                <th className="p-4">{t('Barcode')}</th>
                                <th className="p-4 bg-blue-900/10 text-blue-600">{t('Cost Price')}</th>
                                <th className="p-4 font-black">{t('Profit %')}</th>
                                <th className="p-4 bg-emerald-900/10 text-emerald-600">{t('Sell Price')}</th>
                                <th className="p-4 text-slate-500 font-black">{t('Current Stock')}</th>
                                <th className="p-4">{t('Intake Qty')}</th>
                                <th className="p-4 text-center"></th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {rows
                                .filter(row => {
                                    if (!dateFilter) return true;
                                    // Compare only date part
                                    const rowDate = (row.created_at || '').slice(0,10);
                                    return rowDate === dateFilter;
                                })
                                .map((row, index) => (
                                <tr key={row.id} className="hover:bg-slate-50/50 transition relative group">
                                    <td className="p-4 ltr:pl-8 rtl:pr-8 min-w-[320px] relative">
                                        <div className="relative mb-1">
                                            <span className="text-[9px] font-black text-indigo-400 uppercase tracking-tighter block mb-0.5">{t('Product Name')}</span>
                                            <input
                                                value={row.name}
                                                onChange={e => updateRow(row.id, 'name', e.target.value)}
                                                autoComplete="off"
                                                placeholder={t('Enter product name...')}
                                                className={`w-full border-none bg-indigo-50/20 rounded px-2 py-1 font-black focus:ring-1 focus:ring-indigo-300 text-sm ${row.isMatched ? 'text-indigo-700' : 'text-slate-900'}`}
                                            />
                                            {row.suggestions && row.suggestions.length > 0 && (
                                                <div className="absolute top-12 left-0 w-full bg-white border border-slate-200 rounded-xl shadow-2xl z-50 p-2 space-y-1">
                                                    {row.suggestions.map(s => (
                                                        <button
                                                            key={s.id}
                                                            onClick={() => selectProduct(row.id, s)}
                                                            className="w-full text-left p-3 hover:bg-slate-50 rounded-lg flex justify-between items-center transition group/item"
                                                        >
                                                            <div className="flex flex-col">
                                                                <span className="text-sm font-bold text-slate-700">{s.name}</span>
                                                                <span className="text-[10px] text-slate-400 font-mono italic">{s.barcode || s.sku}</span>
                                                            </div>
                                                            <span className="text-xs font-black text-emerald-600 group-hover/item:scale-110 transition">{parseFloat(s.price).toFixed(2)} CFA</span>
                                                        </button>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <span className="text-[8px] font-bold text-slate-300">REF:</span>
                                            <input
                                                value={row.sku}
                                                onChange={e => updateRow(row.id, 'sku', e.target.value)}
                                                placeholder={t('SKU/Reference')}
                                                className="w-full border-none bg-transparent text-[10px] text-slate-400 focus:ring-0 p-0 uppercase font-mono tracking-tighter"
                                            />
                                        </div>
                                    </td>
                                    <td className="p-4">
                                        <input
                                            value={row.origin || ''}
                                            onChange={e => updateRow(row.id, 'origin', e.target.value)}
                                            placeholder={t('France/Lebanon')}
                                            className="w-24 bg-slate-50 border border-slate-200 px-2 py-1 rounded-lg text-[10px] font-black uppercase focus:border-indigo-400 outline-none"
                                        />
                                    </td>
                                    <td className="p-4">
                                        <div className="flex flex-col gap-1">
                                            <input
                                                value={row.barcode}
                                                onChange={e => handleBarcodeChange(row.id, e.target.value)}
                                                placeholder={t('Barcode')}
                                                className="w-32 bg-slate-50 border border-slate-200 px-2 py-1 rounded-lg text-xs font-mono focus:border-indigo-400 outline-none"
                                            />
                                            {!row.isMatched && (
                                                <button onClick={() => generateBarcode(row.id)} className="text-indigo-600 text-[9px] font-black uppercase text-left hover:text-indigo-800 transition">
                                                    ⚡ {t('Generate Barcode')}
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                    <td className="p-4 bg-blue-50/30">
                                        <input
                                            type="number"
                                            value={row.cost}
                                            onChange={e => updateRow(row.id, 'cost', e.target.value)}
                                            placeholder="0.00"
                                            className="w-20 bg-white border border-blue-200 px-2 py-1 rounded-lg text-xs font-black text-blue-700 focus:ring-2 focus:ring-blue-300 outline-none shadow-sm"
                                        />
                                    </td>
                                    <td className="p-4">
                                        <div className="flex items-center gap-1 group">
                                            <input
                                                type="number"
                                                value={row.profit}
                                                onChange={e => updateRow(row.id, 'profit', e.target.value)}
                                                className="w-16 bg-blue-50 text-blue-400 border border-blue-100 px-2 py-1 rounded-lg text-xs font-black focus:ring-1 focus:ring-blue-400 outline-none"
                                            />
                                            <span className="text-blue-300 font-bold text-xs">%</span>
                                        </div>
                                    </td>
                                    <td className="p-4 bg-emerald-50/30">
                                        <input
                                            type="number"
                                            value={row.price}
                                            onChange={e => updateRow(row.id, 'price', e.target.value)}
                                            placeholder="0.00"
                                            className="w-24 bg-white border border-emerald-200 px-2 py-1 rounded-lg text-xs font-black text-emerald-700 focus:ring-2 focus:ring-emerald-300 outline-none shadow-sm"
                                        />
                                    </td>
                                    <td className="p-4">
                                        <div className="flex flex-col">
                                            <span className="text-[10px] font-black text-slate-400">{row.currentQty}</span>
                                            <span className="text-[8px] text-slate-300 uppercase">{t('on hand')}</span>
                                        </div>
                                    </td>
                                    <td className="p-4">
                                        <input
                                            type="number"
                                            value={row.qty}
                                            onChange={e => updateRow(row.id, 'qty', e.target.value)}
                                            className="w-16 bg-slate-100 border border-slate-200 px-2 py-1 rounded-lg text-xs font-bold outline-none"
                                        />
                                    </td>
                                    <td className="p-4 text-center">
                                        <button onClick={() => removeRow(row.id)} className="text-red-200 hover:text-red-500 transition text-lg mt-1">
                                            ✖
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
</div>
                </div>

                <div className="p-8 bg-slate-50 border-t flex flex-col md:flex-row justify-between items-center gap-6">
                    <button
                        onClick={addRow}
                        className="group flex items-center gap-3 text-indigo-600 font-black uppercase text-xs hover:text-indigo-800 transition"
                    >
                        <span className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center group-hover:bg-indigo-600 group-hover:text-white transition">+</span>
                        {t('Add Another Item')}
                    </button>

                    <button
                        onClick={handleSaveIntake}
                        disabled={loading}
                        className="bg-slate-900 text-white px-12 py-4 rounded-2xl font-black uppercase shadow-2xl hover:bg-black transition-all transform hover:scale-105 disabled:opacity-50 border-b-4 border-slate-700"
                    >
                        {loading ? t('Processing...') : t('Process Intake & Save')}
                    </button>
                </div>
            </div>

            {/* Catalog Overview Section */}
            <div className="mt-12">
                <div className="flex justify-between items-center mb-6">
                    <div>
                        <h3 className="text-2xl font-black text-gray-900 tracking-tight uppercase">{t('Product Catalog Overview')}</h3>
                        <p className="text-xs text-slate-400 font-bold uppercase tracking-widest">{t('Manage persistent cost prices for all items')}</p>
                    </div>
                    <div className="flex gap-2 items-center">
                        <input
                            type="text"
                            placeholder={t('Search products...')}
                            value={productSearch}
                            onChange={e => setProductSearch(e.target.value)}
                            className="border rounded px-2 py-1 text-sm"
                        />
                    </div>
                </div>

                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                    <div className="w-full overflow-x-auto max-w-full">
<table className="w-full text-left">
                        <thead className="bg-slate-50 text-slate-400 text-[10px] font-black uppercase tracking-widest border-b">
                            <tr>
                                <th className="p-4"><input type="checkbox" checked={filteredCatalog.length > 0 && filteredCatalog.every(p => selectedProductIds.includes(p.id))} onChange={e => setSelectedProductIds(e.target.checked ? filteredCatalog.map(p => p.id) : [])} /></th>
                                <th className="p-4 ltr:pl-8 rtl:pr-8">{t('Product')}</th>
                                <th className="p-4">{t('Reference')}</th>
                                <th className="p-4 bg-blue-50/50 text-blue-600 font-black">{t('Current Cost')}</th>
                                <th className="p-4">{t('Current Price')}</th>
                                <th className="p-4">{t('Origin')}</th>
                                <th className="p-4">{t('Barcode')}</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-50">
                            {catalogLoading ? (
                                <tr><td colSpan="7" className="p-10 text-center text-slate-300 font-bold uppercase tracking-widest animate-pulse">{t('Loading Catalog...')}</td></tr>
                            ) : filteredCatalog.length === 0 ? (
                                <tr><td colSpan="7" className="p-10 text-center text-slate-300 italic">{t('No products found in catalog.')}</td></tr>
                            ) : filteredCatalog.map(p => (
                                <tr key={p.id} className="hover:bg-slate-50/30 transition group">
                                    <td className="p-4"><input type="checkbox" checked={selectedProductIds.includes(p.id)} onChange={() => toggleProductSelection(p.id)} /></td>
                                    <td className="p-4 ltr:pl-8 rtl:pr-8">
                                        <div className="flex flex-col">
                                            <span className="text-sm font-black text-slate-800 uppercase leading-none mb-1">{p.name}</span>
                                        </div>
                                    </td>
                                    <td className="p-4">
                                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-tighter">{p.sku}</span>
                                    </td>
                                    <td className="p-4 bg-blue-50/20 group-hover:bg-blue-50/40 transition">
                                        <div className="flex items-center gap-2">
                                            <input
                                                type="number"
                                                step="0.01"
                                                defaultValue={p.cost_price || 0}
                                                onBlur={(e) => handleQuickEditCost(p, e.target.value)}
                                                className="w-20 bg-white border border-blue-100 px-2 py-1 rounded text-xs font-black text-blue-600 focus:ring-2 focus:ring-blue-300 outline-none shadow-sm"
                                            />
                                            <span className="text-[10px] font-black text-blue-300 italic">CFA</span>
                                        </div>
                                    </td>
                                    <td className="p-4">
                                        <span className="text-xs font-bold text-emerald-600">{parseFloat(p.price || 0).toFixed(2)} CFA</span>
                                    </td>
                                    <td className="p-4">
                                        <span className="text-[10px] font-black text-indigo-400 uppercase tracking-widest">{p.origin || '—'}</span>
                                    </td>
                                    <td className="p-4">
                                        <BarcodeGenerator value={p.barcode} />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
</div>
                </div>
            </div>


            {/* ── PRINT REPORT ── */}
            {isPrintModalOpen && (
                <div className="print-area" style={{ fontFamily: 'Arial, sans-serif', color: '#111', fontSize: 11 }}>

                    {/* Header */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', borderBottom: '2px solid #1e293b', paddingBottom: 10, marginBottom: 12 }}>
                        <div>
                            <div style={{ fontSize: 18, fontWeight: 900, letterSpacing: 1, textTransform: 'uppercase' }}>YOUR COMPANY NAME</div>
                            <div style={{ fontSize: 10, color: '#64748b', marginTop: 2 }}>Société d'importation et de commerce</div>
                            <div style={{ fontSize: 10, color: '#64748b' }}>Tel: +213 000 000 000</div>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                            <div style={{ fontSize: 14, fontWeight: 900, color: '#1e293b', textTransform: 'uppercase' }}>Daily Product Intake Report</div>
                            <div style={{ fontSize: 10, color: '#475569', marginTop: 2 }}>Date: <b>{dateFilter}</b></div>
                            <div style={{ fontSize: 10, color: '#475569' }}>Supplier: <b>{supplier || 'All Suppliers'}</b></div>
                            <div style={{ fontSize: 10, color: '#475569' }}>Depot: <b>{depos.find(d => String(d.id) === String(localDepo))?.name || '—'}</b></div>
                            <div style={{ fontSize: 10, color: '#475569' }}>Printed: <b>{new Date().toLocaleString()}</b></div>
                        </div>
                    </div>

                    {/* Products Table */}
                    <div className="w-full overflow-x-auto max-w-full">
<table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 10, marginBottom: 14 }}>
                        <thead>
                            <tr style={{ background: '#1e293b', color: '#fff' }}>
                                <th style={{ padding: '5px 6px', textAlign: 'left', border: '1px solid #334155' }}>#</th>
                                <th style={{ padding: '5px 6px', textAlign: 'left', border: '1px solid #334155' }}>Product Name</th>
                                <th style={{ padding: '5px 6px', textAlign: 'left', border: '1px solid #334155' }}>Reference</th>
                                <th style={{ padding: '5px 6px', textAlign: 'left', border: '1px solid #334155' }}>Barcode</th>
                                <th style={{ padding: '5px 6px', textAlign: 'left', border: '1px solid #334155' }}>Origin</th>
                                <th style={{ padding: '5px 6px', textAlign: 'right', border: '1px solid #334155' }}>Cost Price</th>
                                <th style={{ padding: '5px 6px', textAlign: 'right', border: '1px solid #334155' }}>Margin %</th>
                                <th style={{ padding: '5px 6px', textAlign: 'right', border: '1px solid #334155' }}>Sale Price</th>
                                <th style={{ padding: '5px 6px', textAlign: 'right', border: '1px solid #334155' }}>Qty In</th>
                                <th style={{ padding: '5px 6px', textAlign: 'left', border: '1px solid #334155' }}>Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(() => {
                                // Show catalog products that match dateFilter if available, else show all
                                const printRows = catalog.length > 0
                                    ? catalog.filter(p => {
                                        if (!dateFilter) return true;
                                        // Try to match by any date field on the product or today
                                        const pd = (p.created_at || p.updated_at || '').slice(0, 10);
                                        return pd === dateFilter || !pd;
                                    })
                                    : rows.filter(r => r.name);

                                if (printRows.length === 0) {
                                    return (
                                        <tr>
                                            <td colSpan="10" style={{ textAlign: 'center', padding: 24, color: '#94a3b8', fontStyle: 'italic' }}>
                                                No products found for this date.
                                            </td>
                                        </tr>
                                    );
                                }

                                let totalCost = 0;
                                let totalValue = 0;
                                const tableRows = printRows.map((p, i) => {
                                    const cost  = parseFloat(p.cost_price || p.cost || 0);
                                    const price = parseFloat(p.price || 0);
                                    const qty   = parseFloat(p.qty || p.total_quantity || 1);
                                    const margin = cost > 0 ? (((price - cost) / cost) * 100).toFixed(1) : '—';
                                    totalCost  += cost * qty;
                                    totalValue += price * qty;
                                    const bg = i % 2 === 0 ? '#f8fafc' : '#fff';
                                    return (
                                        <tr key={i} style={{ background: bg }}>
                                            <td style={{ padding: '4px 6px', border: '1px solid #e2e8f0', color: '#94a3b8' }}>{i + 1}</td>
                                            <td style={{ padding: '4px 6px', border: '1px solid #e2e8f0', fontWeight: 700, textTransform: 'uppercase' }}>{p.name || p.product_name || '—'}</td>
                                            <td style={{ padding: '4px 6px', border: '1px solid #e2e8f0', fontFamily: 'monospace', fontSize: 9, color: '#64748b' }}>{p.sku || '—'}</td>
                                            <td style={{ padding: '4px 6px', border: '1px solid #e2e8f0', fontFamily: 'monospace', fontSize: 9 }}>{p.barcode || '—'}</td>
                                            <td style={{ padding: '4px 6px', border: '1px solid #e2e8f0', textTransform: 'uppercase', fontSize: 9, color: '#6366f1' }}>{p.origin || '—'}</td>
                                            <td style={{ padding: '4px 6px', border: '1px solid #e2e8f0', textAlign: 'right', color: '#1d4ed8', fontWeight: 700 }}>{cost.toFixed(2)}</td>
                                            <td style={{ padding: '4px 6px', border: '1px solid #e2e8f0', textAlign: 'right', color: margin !== '—' && parseFloat(margin) < 10 ? '#dc2626' : '#16a34a' }}>{margin !== '—' ? margin + '%' : '—'}</td>
                                            <td style={{ padding: '4px 6px', border: '1px solid #e2e8f0', textAlign: 'right', color: '#059669', fontWeight: 700 }}>{price.toFixed(2)}</td>
                                            <td style={{ padding: '4px 6px', border: '1px solid #e2e8f0', textAlign: 'right', fontWeight: 700 }}>{qty}</td>
                                            <td style={{ padding: '4px 6px', border: '1px solid #e2e8f0' }}>
                                                <span style={{ background: price > cost ? '#dcfce7' : '#fee2e2', color: price > cost ? '#15803d' : '#dc2626', borderRadius: 4, padding: '1px 5px', fontSize: 9, fontWeight: 700 }}>
                                                    {price > cost ? '✔ OK' : '⚠ CHECK'}
                                                </span>
                                            </td>
                                        </tr>
                                    );
                                });

                                return (
                                    <>
                                        {tableRows}
                                        {/* Totals row */}
                                        <tr style={{ background: '#f1f5f9', fontWeight: 700 }}>
                                            <td colSpan="5" style={{ padding: '5px 6px', border: '1px solid #e2e8f0', textAlign: 'right', color: '#475569' }}>TOTALS ({printRows.length} items)</td>
                                            <td style={{ padding: '5px 6px', border: '1px solid #e2e8f0', textAlign: 'right', color: '#1d4ed8' }}>{totalCost.toFixed(2)}</td>
                                            <td style={{ padding: '5px 6px', border: '1px solid #e2e8f0' }}></td>
                                            <td style={{ padding: '5px 6px', border: '1px solid #e2e8f0', textAlign: 'right', color: '#059669' }}>{totalValue.toFixed(2)}</td>
                                            <td colSpan="2" style={{ padding: '5px 6px', border: '1px solid #e2e8f0' }}></td>
                                        </tr>
                                    </>
                                );
                            })()}
                        </tbody>
                    </table>
</div>

                    {/* Summary boxes */}
                    <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
                        {[
                            { label: 'Total Products', value: catalog.length || rows.filter(r => r.name).length, color: '#1e293b' },
                            { label: t('Total Cost Value'), value: catalog.reduce((s, p) => s + parseFloat(p.cost_price || 0), 0).toFixed(2) + ' CFA', color: '#1d4ed8' },
                            { label: t('Total Sale Value'), value: catalog.reduce((s, p) => s + parseFloat(p.price || 0), 0).toFixed(2) + ' CFA', color: '#059669' },
                            { label: 'Avg. Margin', value: (() => { const items = catalog.filter(p => parseFloat(p.cost_price) > 0); if (!items.length) return '—'; const avg = items.reduce((s, p) => s + (((parseFloat(p.price) - parseFloat(p.cost_price)) / parseFloat(p.cost_price)) * 100), 0) / items.length; return avg.toFixed(1) + '%'; })(), color: '#7c3aed' },
                        ].map((box, i) => (
                            <div key={i} style={{ flex: 1, border: '1px solid #e2e8f0', borderRadius: 6, padding: '8px 10px', background: '#f8fafc' }}>
                                <div style={{ fontSize: 9, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 2 }}>{box.label}</div>
                                <div style={{ fontSize: 14, fontWeight: 900, color: box.color }}>{box.value}</div>
                            </div>
                        ))}
                    </div>

                    {/* Signatures */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 24, borderTop: '1px solid #e2e8f0', paddingTop: 16 }}>
                        {['Prepared By', 'Verified By', 'Manager Approval'].map((label, i) => (
                            <div key={i} style={{ textAlign: 'center', width: '28%' }}>
                                <div style={{ borderBottom: '1px solid #334155', marginBottom: 4, height: 32 }}></div>
                                <div style={{ fontSize: 9, color: '#64748b', fontWeight: 700 }}>{label}</div>
                                <div style={{ fontSize: 8, color: '#94a3b8' }}>Name & Date</div>
                            </div>
                        ))}
                    </div>

                    {/* Footer */}
                    <div style={{ marginTop: 14, borderTop: '1px solid #e2e8f0', paddingTop: 6, display: 'flex', justifyContent: 'space-between', fontSize: 9, color: '#94a3b8' }}>
                        <span>Generated by ERP System · {new Date().toLocaleString()}</span>
                        <span>CONFIDENTIAL — Internal Use Only</span>
                    </div>
                </div>
            )}
        </div>
    );
};

// BarcodeGenerator that works for print (renders as <img> for print)
const BarcodeGenerator = ({ value }) => {
    const ref = useRef(null);
    const [imgUrl, setImgUrl] = useState("");
    useEffect(() => {
        if (value && ref.current) {
            try {
                let format = "CODE128";
                // Only use EAN13 for strictly valid 13-digit codes with correct check digit
                if (/^\d{13}$/.test(value)) {
                    const digits = value.split('').map(Number);
                    const check = (10 - ((digits.slice(0,12).reduce((s, d, i) => s + d * (i % 2 === 0 ? 1 : 3), 0)) % 10)) % 10;
                    if (check === digits[12]) format = "EAN13";
                }
                JsBarcode(ref.current, value, {
                    format,
                    width: 2,
                    height: 40,
                    displayValue: true,
                    fontSize: 12,
                    margin: 0,
                    background: "#ffffff"
                });
                // For print: convert SVG to data URL
                setTimeout(() => {
                    if (ref.current) {
                        const svg = ref.current;
                        const xml = new XMLSerializer().serializeToString(svg);
                        const url = "data:image/svg+xml;base64," + btoa(xml);
                        setImgUrl(url);
                    }
                }, 100);
            } catch (e) {
                console.warn("Barcode rendering failed", e);
            }
        }
    }, [value]);

    if (!value) return <div className="text-[8px] text-slate-300 py-4 italic">{('No Barcode')}</div>;
    return (
        <div className="flex justify-center bg-white">
            <svg ref={ref} style={{ display: 'block' }}></svg>
            {/* For print: show as image */}
            {imgUrl && <img src={imgUrl} alt="barcode" className="hidden print:block" style={{ width: 120, height: 40 }} />}
        </div>
    );
};

export default StockIntake;
